// Centralized translations for all i18n keys
export const translations = {
  en: {
    // Common translations
    common: {
      heroTitle: "Your Reliable Ride Around Koh Phangan",
      heroSubtitle: "Safe, comfortable, and always on time. Book your island taxi in seconds.",
      bookNowButton: "Book Taxi Now",
      viewPrices: "View Prices"
    },

    // Navigation translations
    navigation: {
      home: "Order Taxi",
      prices: "Prices",
      services: "Services",
      contact: "Contact"
    },

    // Booking form translations
    booking: {
      form: {
        tripDetailsTitle: 'Trip Details',
        pickupLabel: 'Pickup Location',
        pickupPlaceholder: 'Enter pickup location',
        destinationLabel: 'Destination',
        destinationPlaceholder: 'Where are you going?',
        dateLabel: 'Date',
        datePlaceholder: 'Select date',
        timeLabel: 'Time',
        passengersLabel: 'Number of Passengers',
        passengersPlaceholder: 'Enter number of passengers (1-10)',
        passengersText: 'passengers',
        passengerText: 'passenger',
        largeGroupNotice: 'Large group - Van service recommended',
        contactLabel: 'Contact Information',
        reviewMessage: 'Please review your booking details before confirming. Our team will contact you shortly to confirm your ride.',
        scheduledBooking: 'Schedule for Later',
        scheduledDescription: 'Choose date and time',
        immediateBooking: 'Order Now',
        immediateDescription: 'Nearest available vehicle',
        immediateNoticeTitle: 'Instant Pickup Request',
        immediateNoticeMessage: 'The nearest available driver will be notified immediately. Typical pickup time: 5-15 minutes.',
        estimatedPickup: 'Estimated Pickup',
        asapPickup: 'As soon as possible (5-15 minutes)',
        confirmButton: 'Confirm Booking',
        fillAllFields: 'Please fill all required fields',
        confirmHelper: 'By confirming, you agree to our terms and conditions'
      },
      confirmation: {
        title: 'Booking Confirmed!',
        message: 'Your transfer has been successfully booked. You will receive a confirmation shortly.',
        immediateMessage: 'Your immediate transfer request has been sent. A nearby driver will contact you shortly!',
        closeButton: 'Done'
      }
    },

    // Services page translations
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'Capacity',
      vehicleType: 'Vehicle Type',
      availability: 'Availability',
      startingAt: 'Starting At',
      bookNow: 'Book Now',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Door-to-door service',
          'Trusted drivers',
          'Real-time confirmation'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },

    // Why Choose Us section translations
    whyChooseUs: {
      sectionTitle: 'Why Choose Us',
      sectionSubtitle: 'Experience the difference with our professional taxi service',
      benefits: {
        licensedInsured: {
          title: 'Licensed & Insured',
          description: 'All our drivers are licensed professionals with comprehensive insurance coverage for your peace of mind.'
        },
        fixedPricing: {
          title: 'Fixed Pricing',
          description: 'No surprises! Our transparent pricing means you know exactly what you\'ll pay before you book.'
        },
        availability: {
          title: '24/7 Availability',
          description: 'Day or night, we\'re here for you. Book anytime, anywhere on the island.'
        },
        multiLanguage: {
          title: 'Multi-Language Support',
          description: 'Our drivers speak English, Thai, and several other languages to serve you better.'
        },
        professionalDrivers: {
          title: 'Professional Drivers',
          description: 'Experienced, courteous drivers who know every corner of Koh Phangan.'
        },
        easyBooking: {
          title: 'Easy Booking',
          description: 'Book in seconds through our simple booking system. No account required.'
        }
      }
    },

    // Route price list translations
    routePriceList: {
      pageTitle: 'Route Price List',
      prices: 'Route Price List',
      pricingNotice: 'Fixed prices per route. No hidden fees. All prices shown in THB.',
      currencyNote: 'All prices shown in THB.',
      serviceType: 'Service Type',
      allServices: 'All Services',
      allRoutes: 'All routes',
      popular: 'Popular route',
      popularRoutes: 'Popular Routes',
      popularRoutesSubtitle: 'Most traveled routes by our customers',
      bookNow: 'Book this route',
      noRoutesFound: 'No Routes Found',
      noRoutesMessage: 'Try adjusting your filters or browse all available routes',
      viewAllRoutes: 'View All Routes',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'Island Taxi',
      sharedShuttle: 'Shared Shuttle',
      privateTransfer: 'Private Transfer',
      vipLuxury: 'VIP Luxury',
      airportTransfer: 'Airport Transfer',
      cargoTaxi: 'Cargo Taxi',
      bikeTaxi: 'Bike Taxi',
      taxiBoat: 'Taxi Boat',
      deliveryService: 'Delivery Service',
      paymentDisclaimer: 'Important Payment Information',
      currencyFootnote: 'All transactions are processed in Thai Baht. Displayed prices in other currencies are estimates based on current exchange rates and may vary at time of payment.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Book this route',
      bookPrivateRide: 'Book private ride',
      bookBoat: 'Book boat',
      bookCargoTaxi: 'Book cargo taxi',
      bookDelivery: 'Book delivery',
      cargoTaxiTitle: 'Cargo Taxi Service',
      cargoTaxiDescription: 'Transport for large items and equipment. Available island-wide.',
      deliveryServiceTitle: 'Island Delivery Service',
      deliveryServiceDescription: 'Fast delivery anywhere on Phangan. Fixed rates per zone.',
      
      // NEW: Service metadata translations
      meta: {
        // Service types
        sharedTaxi: 'Shared Taxi',
        privateTaxi: 'Private Taxi',
        vipTaxi: 'VIP Taxi',
        cargoTaxi: 'Cargo Taxi',
        taxiBoat: 'Taxi Boat',
        delivery: 'Delivery',
        
        // Pricing modifiers
        perPerson: 'Per Person',
        perVehicle: 'Per Vehicle',
        perTrip: 'Per Trip',
        
        // Duration ranges
        duration_15_20: '15-20 min',
        duration_20_30: '20-30 min',
        duration_30_40: '30-40 min',
        duration_40_50: '40-50 min',
        duration_50_60: '50-60 min',
        
        // Passenger counts
        passengers_1: '1 Passenger',
        passengers_2: '2 Passengers',
        passengers_4: '4 Passengers',
        passengers_6: '6 Passengers',
        passengers_1_4: '1-4 Passengers',
        passengers_1_6: '1-6 Passengers',
        passengers_4_8: '4-8 Passengers'
      }
    },

    // Zones translations
    zones: {
      'Thong Sala': 'Thong Sala',
      'Baan Tai': 'Baan Tai',
      'Baan Kai': 'Baan Kai',
      'Haad Rin': 'Haad Rin',
      'Srithanu': 'Srithanu',
      'Haad Yao': 'Haad Yao',
      'Haad Salad': 'Haad Salad',
      'Mae Haad': 'Mae Haad',
      'Chaloklum': 'Chaloklum',
      'Bottle Beach': 'Bottle Beach',
      'Thong Nai Pan': 'Thong Nai Pan',
      'Amsterdam Bar': 'Amsterdam Bar'
    }
  },

  ru: {
    common: {
      heroTitle: "Ваш Надежный Такси на Ко Пангане",
      heroSubtitle: "Безопасно, комфортно и всегда вовремя. Забронируйте такси за секунды.",
      bookNowButton: "Заказать Такси",
      viewPrices: "Посмотреть Цены"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Цены",
      services: "Услуги",
      contact: "Контакты"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Детали поездки',
        pickupLabel: 'Место посадки',
        pickupPlaceholder: 'Введите место посадки',
        destinationLabel: 'Пункт назначения',
        destinationPlaceholder: 'Куда вы едете?',
        dateLabel: 'Дата',
        datePlaceholder: 'Выберите дату',
        timeLabel: 'Время',
        passengersLabel: 'Количество пассажиров',
        passengersPlaceholder: 'Введите количество пассажиров (1-10)',
        passengersText: 'пассажиров',
        passengerText: 'пассажир',
        largeGroupNotice: 'Большая группа - рекомендуется микроавтобус',
        contactLabel: 'Контактная информация',
        reviewMessage: 'Пожалуйста, проверьте детали бронирования перед подтверждением. Наша команда свяжется с вами в ближайшее время для подтверждения поездки.',
        scheduledBooking: 'Запланировать на потом',
        scheduledDescription: 'Выберите дату и время',
        immediateBooking: 'Заказать сейчас',
        immediateDescription: 'Ближайшая доступная машина',
        immediateNoticeTitle: 'Мгновенный заказ',
        immediateNoticeMessage: 'Ближайший доступный водитель будет уведомлен немедленно. Обычное время прибытия: 5-15 минут.',
        estimatedPickup: 'Ожидаемое прибытие',
        asapPickup: 'Как можно скорее (5-15 минут)',
        confirmButton: 'Подтвердить бронирование',
        fillAllFields: 'Пожалуйста, заполните все обязательные поля',
        confirmHelper: 'Подтверждая, вы соглашаетесь с нашими условиями'
      },
      confirmation: {
        title: 'Бронирование подтверждено!',
        message: 'Ваш трансфер успешно забронирован. Вы получите подтверждение в ближайшее время.',
        immediateMessage: 'Ваш запрос на немедленный трансфер отправлен. Ближайший водитель свяжется с вами в ближайшее время!',
        closeButton: 'Готово'
      }
    },
    services: {
      sectionTitle: 'Наше такси',
      sectionIntro: 'Выберите тип такси, который подходит для вашей поездки.',
      servicesAvailable: 'доступно услуг',
      capacity: 'Вместимость',
      vehicleType: 'Тип транспорта',
      availability: 'Доступность',
      startingAt: 'Цена от',
      bookNow: 'Забронировать',
      stats: {
        title: 'Статистика услуг',
        activeVehicles: 'Активные авто',
        happyCustomers: 'Задоволенных клиентов',
        islandCoverage: 'Покрытие острова',
        serviceAvailable: 'Услуга доступна',
        drivers: 'Активных водителей',
        trips: 'Поездок в день',
        rating: 'Средний рейтинг',
        coverage: 'Покрытие острова'
      },
      filters: {
        allServices: 'Все услуги',
        popular: 'Популярные',
        transfers: 'Трансферы',
        private: 'Приватные'
      },
      sharedTaxi: {
        title: 'Совместное такси',
        description: 'Доступные совместные поездки между популярными местами на Ко Пангане.',
        priceRange: '฿150-300',
        features: [
          'Фиксированная цена на человека',
          'Быстрая посадка с основных мест',
          'Автомобили с кондиционером',
          'Идеально для одиночных путешественников'
        ]
      },
      privateTaxi: {
        title: 'Приватное такси',
        description: 'Ваш собственный водитель для быстрого и комфортного транспорта в любом месте на острове.',
        priceRange: '฿400-800',
        features: [
          'Прямой маршрут без остановок',
          'Чистые и комфортные автомобили',
          'Гибкая посадка и высадка',
          'Отлично для семей и небольших групп'
        ]
      },
      islandTaxi: {
        title: 'Островное такси',
        description: 'Трансферы из точки в точку по всему острову, доступные 24/7.',
        priceRange: '฿200-600',
        features: [
          'Фиксированная прозрачная цена',
          'Местные опытные водители',
          'Быстрое прибытие',
          'Покрывает все пляжи и деревни'
        ]
      },
      airportTransfer: {
        title: 'Трансфер до аэропорта',
        description: 'Гладкое соединение между Ко Панганом и аэропортом Самуи — без стресса путешествия.',
        priceRange: '฿1,200-2,500',
        features: [
          'Координация лодки + такси',
          'Гарантированное прибытие вовремя',
          'Помощь с багажем',
          'Идеально для ранних рейсов'
        ]
      },
      cargoDelivery: {
        title: 'Доставка грузов',
        description: 'Быстрая и безопасная доставка посылок, багажа и личных вещей по всему острову.',
        priceRange: '฿150-500',
        features: [
          'Доставка в тот же день',
          'Услуга от двери до двери',
          'Доверенные водители',
          'Подтверждение в реальном времени'
        ]
      },
      taxiBoat: {
        title: 'Такси-лодка',
        description: 'Прямой трансфер лодкой до отдаленных пляжей и бухт вокруг Ко Пангане.',
        priceRange: '฿500-2,000',
        features: [
          'Быстрый доступ до Хаад Юань, Хаад Тиен, Боттл Бич',
          'Безопасные лицензированные операторы лодок',
          'Отправление с проверкой погоды',
          'Идеально для вечеринок и отдаленных курортов'
        ]
      },
      vipTaxi: {
        title: 'VIP такси',
        description: 'Премиум транспорт для максимального комфорта, приватности и стиля.',
        priceRange: '฿1,000-2,000',
        features: [
          'Новые роскошные автомобили',
          'Профессиональные VIP водители',
          'Дополнительное пространство для багажа',
          'Приоритетная посадка'
        ]
      },
      deliveryService: {
        title: 'Услуга доставки',
        description: 'Быстрая доставка товаров, покупок и необходимых вещей в любом месте на острове.',
        priceRange: '฿100-400',
        features: [
          'Покрытие всего острова',
          'Фиксированная низкая цена',
          'Надежная обработка',
          'Быстрые сроки доставки'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Трансфер до причала',
        description: 'Прямые трансферы с/до причала Тонг Сала и Хаад Рин',
        priceRange: '฿200-600',
        features: [
          'Услуга встречи',
          'Помощь с багажем',
          'Отслеживание расписания паромов',
          'Прямые маршруты'
        ]
      },
      privateRide: {
        title: 'Приватная поездка',
        description: 'Премиум приватный транспорт для роскошного путешествия',
        priceRange: '฿500-1,500',
        features: [
          'Премиум автомобили',
          'Удостоверенный водитель',
          'Гибкое планирование',
          'VIP обслуживание'
        ]
      },
      groupTransfer: {
        title: 'Групповой трансфер',
        description: 'Просторные микроавтобусы идеально подходят для групп и семей',
        priceRange: '฿800-2,000',
        features: [
          'До 12 пассажиров',
          'Дополнительное пространство для багажа',
          'Опции разделенной оплаты',
          'Групповые скидки'
        ]
      },
      bikeTaxi: {
        title: 'Мото-такси',
        description: 'Быстрая услуга мото-такси для одиночных путешественников',
        priceRange: '฿50-300',
        features: [
          'Самый быстрый вариант',
          'Преимущество в трафике',
          'Шлем предоставляется',
          'Короткие расстояния'
        ]
      }
    },
    whyChooseUs: {
      sectionTitle: 'Почему выбирают нас',
      sectionSubtitle: 'Почувствуйте разницу с нашим профессиональным сервисом такси',
      benefits: {
        licensedInsured: {
          title: 'Лицензия и страховка',
          description: 'Все наши водители - лицензированные профессионалы с полным страховым покрытием для вашего спокойствия.'
        },
        fixedPricing: {
          title: 'Фиксированные цены',
          description: 'Никаких сюрпризов! Прозрачное ценообразование означает, что вы знаете точную стоимость до бронирования.'
        },
        availability: {
          title: 'Доступны 24/7',
          description: 'День и ночь, мы здесь для вас. Бронируйте в любое время в любой точке острова.'
        },
        multiLanguage: {
          title: 'Многоязычная поддержка',
          description: 'Наши водители говорят на английском, тайском и нескольких других языках для вашего удобства.'
        },
        professionalDrivers: {
          title: 'Профессиональные водители',
          description: 'Опытные, вежливые водители, которые знают каждый уголок Ко Пангана.'
        },
        easyBooking: {
          title: 'Простое бронирование',
          description: 'Забронируйте за секунды через нашу простую систему бронирования. Регистрация не требуется.'
        }
      }
    },
    routePriceList: {
      pageTitle: 'Цены на маршруты | Ко Панган',
      prices: 'Прайс-лист маршрутов',
      pricingNotice: 'Фиксированные цены. Без скрытых комиссий. Указаны в THB.',
      currencyNote: 'Все цены в THB.',
      serviceType: 'Тип услуги',
      allServices: 'Все услуги',
      allRoutes: 'Все маршруты',
      popular: 'Популярный',
      popularRoutes: 'Популярные маршруты',
      popularRoutesSubtitle: 'Самые востребованные направления',
      bookNow: 'Забронировать маршрут',
      noRoutesFound: 'Маршруты не найдены',
      noRoutesMessage: 'Измените фильтры или просмотрите все маршруты',
      viewAllRoutes: 'Все маршруты',
      searchPlaceholder: 'Поиск по локации...',
      searchLabel: 'Поиск маршрутов',
      resultsFound: 'маршрутов найдено',
      islandTaxi: 'Такси по острову',
      sharedShuttle: 'Общий шаттл',
      privateTransfer: 'Приват. трансфер',
      vipLuxury: 'VIP класс',
      airportTransfer: 'В аэропорт',
      cargoTaxi: 'Грузовое такси',
      bikeTaxi: 'Мототакси',
      taxiBoat: 'Такси-лодка',
      deliveryService: 'Доставка',
      paymentDisclaimer: 'Важная информация об оплате',
      currencyFootnote: 'Все платежи в THB. Цены в др. валютах — примерные.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Забронировать',
      bookPrivateRide: 'Заказать приватную',
      bookBoat: 'Заказать лодку',
      bookCargoTaxi: 'Заказать груз. такси',
      bookDelivery: 'Заказать доставку',
      cargoTaxiTitle: 'Грузовое такси',
      cargoTaxiDescription: 'Перевозка крупных вещей по всему острову.',
      deliveryServiceTitle: 'Доставка по острову',
      deliveryServiceDescription: 'Быстрая доставка по Пангану. Фиксированные цены.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'Попутное такси',
        privateTaxi: 'Индивидуальное такси',
        vipTaxi: 'VIP такси',
        cargoTaxi: 'Грузовое такси',
        taxiBoat: 'Такси-лодка',
        delivery: 'Доставка',
        
        perPerson: 'с человека',
        perVehicle: 'за машину',
        perTrip: 'за поездку',
        
        duration_15_20: '15–20 мин',
        duration_20_30: '20–30 мин',
        duration_30_40: '30–40 мин',
        duration_40_50: '40–50 мин',
        duration_50_60: '50–60 мин',
        
        passengers_1: '1 пассажир',
        passengers_2: '2 пассажира',
        passengers_4: '4 пассажира',
        passengers_6: '6 пассажиров',
        passengers_1_4: '1–4 пассажира',
        passengers_1_6: '1–6 пассажиров',
        passengers_4_8: '4–8 пассажиров'
      }
    },
    zones: {
      'Thong Sala': 'Тонг Сала',
      'Baan Tai': 'Баан Тай',
      'Baan Kai': 'Баан Кай',
      'Haad Rin': 'Хаад Рин',
      'Srithanu': 'Шритану',
      'Haad Yao': 'Хаад Яо',
      'Haad Salad': 'Хаад Салад',
      'Mae Haad': 'Мае Хаад',
      'Chaloklum': 'Чалоклум',
      'Bottle Beach': 'Боттл Бич',
      'Thong Nai Pan': 'Тонг Най Пан',
      'Amsterdam Bar': 'Амстердам Бар'
    }
  },

  th: {
    common: {
      heroTitle: "บริการแท็กซี่ที่เชื่อถือได้บนเกาะพะงัน",
      heroSubtitle: "ปลอดภัย สะดวกสบาย และตรงเวลาเสมอ จองแท็กซี่ได้ในไม่กี่วินาที",
      bookNowButton: "จองแท็กซี่ทันที",
      viewPrices: "ดูราคา"
    },
    navigation: {
      home: "Order Taxi",
      prices: "ราคา",
      services: "บริการ",
      contact: "ติดต่อ"
    },
    booking: {
      form: {
        tripDetailsTitle: 'รายละเอียดการเดินทาง',
        pickupLabel: 'จุดรับ',
        pickupPlaceholder: 'ป้อนสถานที่รับ',
        destinationLabel: 'จุดหมาย',
        destinationPlaceholder: 'คุณจะไปที่ไหน?',
        dateLabel: 'วันที่',
        datePlaceholder: 'เลือกวันที่',
        timeLabel: 'เวลา',
        passengersLabel: 'จำนวนผู้โดยสาร',
        passengersPlaceholder: 'ป้อนจำนวนผู้โดยสาร (1-10)',
        passengersText: 'ผู้โดยสาร',
        passengerText: 'ผู้โดยสาร',
        largeGroupNotice: 'กลุ่มใหญ่ - แนะนำบริการรถตู้',
        contactLabel: 'ข้อมูลติดต่อ',
        reviewMessage: 'โปรดตรวจสอบรายละเอียดการจองก่อนยืนยัน ทีมงานของเราจะติดต่อคุณในเร็วๆ นี้เพื่อยืนยันการเดินทาง',
        scheduledBooking: 'จองล่วงหน้า',
        scheduledDescription: 'เลือกวันที่และเวลา',
        immediateBooking: 'จองทันที',
        immediateDescription: 'รถที่ใกล้ที่สุด',
        immediateNoticeTitle: 'การจองแบบทันที',
        immediateNoticeMessage: 'คนขับที่ใกล้ที่สุดจะได้รับการแจ้งเตือนทันที เวลารับโดยประมาณ: 5-15 นาที',
        estimatedPickup: 'เวลารับโดยประมาณ',
        asapPickup: 'โดยเร็วที่สุด (5-15 นาที)',
        confirmButton: 'ยืนยันการจอง',
        fillAllFields: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        confirmHelper: 'การยืนยันหมายถึงคุณยอมรับข้อกำหนดและเงื่อนไขของเรา'
      },
      confirmation: {
        title: 'การจองเสร็จสมบูรณ์!',
        message: 'การเดินทางของคุณถูกจองเรียบร้อยแล้ว คุณจะได้รับการยืนยันในเร็วๆ นี้',
        immediateMessage: 'คำขอการเดินทางแบบทันทีของคุณถูกส่งแล้ว คนขับที่ใกล้ที่สุดจะติดต่อคุณในเร็วๆ นี้!',
        closeButton: 'เสร็จสิ้น'
      }
    },
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'ความจุ',
      vehicleType: 'ประเภทรถ',
      availability: 'ความพร้อม',
      startingAt: 'เริ่มต้นที่',
      bookNow: 'จองเลย',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Door-to-door service',
          'Trusted drivers',
          'Real-time confirmation'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },
    whyChooseUs: {
      sectionTitle: 'ทำไมต้องเลือกเรา',
      sectionSubtitle: 'สัมผัสความแตกต่างกับบริการแท็กซี่มืออาชีพของเรา',
      benefits: {
        licensedInsured: {
          title: 'มีใบอนุญาตและประกัน',
          description: 'คนขับของเราทุกคนเป็นมืออาชีพที่มีใบอนุญาตพร้อมประกันภัยครอบคลุมเพื่อความสบายใจของคุณ'
        },
        fixedPricing: {
          title: 'ราคาคงที่',
          description: 'ไม่มีค่าใช้จ่ายซ่อนเร้น! ราคาโปร่งใสหมายความว่าคุณจะรู้ราคาที่แน่นอนก่อนจอง'
        },
        availability: {
          title: 'บริการตลอด 24/7',
          description: 'ทั้งกลางวันและกลางคืน เราพร้อมให้บริการคุณ จองได้ทุกที่ทุกเวลาบนเกาะ'
        },
        multiLanguage: {
          title: 'รองรับหลายภาษา',
          description: 'คนขับของเราพูดได้ทั้งอังกฤษ ไทย และภาษาอื่นๆ อีกหลายภาษาเพื่อให้บริการคุณได้ดียิ่งขึ้น'
        },
        professionalDrivers: {
          title: 'คนขับมืออาชีพ',
          description: 'คนขับที่มีประสบการณ์ สุภาพ และรู้จักทุกมุมของเกาะพะงัน'
        },
        easyBooking: {
          title: 'จองง่าย',
          description: 'จองได้ภายในไม่กี่วินาทีผ่านระบบจองที่เรียบง่าย ไม่ต้องสมัครสมาชิก'
        }
      }
    },
    routePriceList: {
      pageTitle: 'ราคาเส้นทาง | เกาะพะงัน',
      prices: 'รายการราคาเส้นทาง',
      pricingNotice: 'ราคาคงที่ ไม่มีค่าธรรมเนียมซ่อนเร้น ระบุเป็นบาท',
      currencyNote: 'ราคาทั้งหมดเป็นบาท',
      serviceType: 'ประเภทบริการ',
      allServices: 'บริการทั้งหมด',
      allRoutes: 'เส้นทางทั้งหมด',
      popular: 'ยอดนิยม',
      popularRoutes: 'เส้นทางยอดนิยม',
      popularRoutesSubtitle: 'เส้นทางที่นิยมมากที่สุด',
      bookNow: 'จองเส้นทางนี้',
      noRoutesFound: 'ไม่พบเส้นทาง',
      noRoutesMessage: 'ลองปรับตัวกรองหรือดูทั้งหมด',
      viewAllRoutes: 'ดูทั้งหมด',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'แท็กซี่เกาะ',
      sharedShuttle: 'รถรับส่งรวม',
      privateTransfer: 'รถเช่าส่วนตัว',
      vipLuxury: 'วีไอพี',
      airportTransfer: 'รับส่งสนามบิน',
      cargoTaxi: 'รถขนส่งสินค้า',
      bikeTaxi: 'มอเตอร์ไซค์รับจ้าง',
      taxiBoat: 'เรือแท็กซี่',
      deliveryService: 'บริการจัดส่ง',
      paymentDisclaimer: 'ข้อมูลการชำระเงินที่สำคัญ',
      currencyFootnote: 'ทำรายการเป็นบาท ราคาสกุลอื่นเป็นประมาณการ',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'จองเลย',
      bookPrivateRide: 'จองรถส่วนตัว',
      bookBoat: 'จองเรือ',
      bookCargoTaxi: 'จองรถขนส่ง',
      bookDelivery: 'จองจัดส่ง',
      cargoTaxiTitle: 'รถขนส่งสินค้า',
      cargoTaxiDescription: 'ขนส่งของขนาดใหญ่ทั่วเกาะ',
      deliveryServiceTitle: 'บริการจัดส่งเกาะ',
      deliveryServiceDescription: 'ส่งด่วนทั่วพะงัน ราคาคงที่ตามโซน',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'แท็กซี่รวม',
        privateTaxi: 'แท็กซี่ส่วนตัว',
        vipTaxi: 'VIP แท็กซี่',
        cargoTaxi: 'แท็กซี่ขนส่ง',
        taxiBoat: 'เรือแท็กซี่',
        delivery: 'บริการจัดส่ง',
        
        perPerson: 'ต่อคน',
        perVehicle: 'ต่อคัน',
        perTrip: 'ต่อเที่ยว',
        
        duration_15_20: '15–20 นาที',
        duration_20_30: '20–30 นาที',
        duration_30_40: '30–40 นาที',
        duration_40_50: '40–50 นาที',
        duration_50_60: '50–60 นาที',
        
        passengers_1: '1 คน',
        passengers_2: '2 คน',
        passengers_4: '4 คน',
        passengers_6: '6 คน',
        passengers_1_4: '1–4 คน',
        passengers_1_6: '1–6 คน',
        passengers_4_8: '4–8 คน'
      }
    },
    zones: {
      'Thong Sala': 'ท้องศาลา',
      'Baan Tai': 'บ้านใต้',
      'Baan Kai': 'บ้านไก่',
      'Haad Rin': 'หาดริ้น',
      'Srithanu': 'ศรีธนู',
      'Haad Yao': 'หาดยาว',
      'Haad Salad': 'หาดสลัด',
      'Mae Haad': 'แม่หาด',
      'Chaloklum': 'เฉวงน้อย',
      'Bottle Beach': 'หาดขวด',
      'Thong Nai Pan': 'ท้องนายปาน',
      'Amsterdam Bar': 'บาร์อัมสเตอร์ดัม'
    }
  },

  de: {
    common: {
      heroTitle: "Ihre Zuverlässige Fahrt auf Koh Phangan",
      heroSubtitle: "Sicher, komfortabel und immer pünktlich. Buchen Sie Ihr Insel-Taxi in Sekunden.",
      bookNowButton: "Taxi Buchen",
      viewPrices: "Preise Anzeigen"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Preise",
      services: "Dienstleistungen",
      contact: "Kontakt"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Reisedetails',
        pickupLabel: 'Abholort',
        pickupPlaceholder: 'Abholort eingeben',
        destinationLabel: 'Zielort',
        destinationPlaceholder: 'Wohin möchten Sie?',
        dateLabel: 'Datum',
        datePlaceholder: 'Datum wählen',
        timeLabel: 'Uhrzeit',
        passengersLabel: 'Anzahl der Passagiere',
        passengersPlaceholder: 'Anzahl der Passagiere (1-10)',
        passengersText: 'Passagiere',
        passengerText: 'Passagier',
        largeGroupNotice: 'Große Gruppe - Fahrzeugempfehlung',
        contactLabel: 'Kontaktinformationen',
        reviewMessage: 'Bitte überprüfen Sie Ihre Buchungsdetails vor der Bestätigung. Unser Team wird sich in Kürze mit Ihnen in Verbindung setzen, um Ihre Fahrt zu bestätigen.',
        scheduledBooking: 'Später planen',
        scheduledDescription: 'Datum und Uhrzeit wählen',
        immediateBooking: 'Jetzt bestellen',
        immediateDescription: 'Nächstgelegenes verfügbares Fahrzeug',
        immediateNoticeTitle: 'Sofortige Abholanfrage',
        immediateNoticeMessage: 'Der nächstgelegene verfügbare Fahrer wird sofort benachrichtigt. Typische Abholzeit: 5-15 Minuten.',
        estimatedPickup: 'Geschätzte Abholung',
        asapPickup: 'So schnell wie möglich (5-15 Minuten)',
        confirmButton: 'Buchung bestätigen',
        fillAllFields: 'Bitte füllen Sie alle erforderlichen Felder aus',
        confirmHelper: 'Mit der Bestätigung stimmen Sie unseren Bedingungen zu'
      },
      confirmation: {
        title: 'Buchung bestätigt!',
        message: 'Ihr Transfer wurde erfolgreich gebucht. Sie erhalten in Kürze eine Bestätigung.',
        immediateMessage: 'Ihre sofortige Transferanfrage wurde gesendet. Ein nahegelegener Fahrer wird Sie in Kürze kontaktieren!',
        closeButton: 'Fertig'
      }
    },
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'Kapazität',
      vehicleType: 'Fahrzeugtyp',
      availability: 'Verfügbarkeit',
      startingAt: 'Ab',
      bookNow: 'Jetzt buchen',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Door-to-door service',
          'Trusted drivers',
          'Real-time confirmation'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },
    whyChooseUs: {
      sectionTitle: 'Warum uns wählen',
      sectionSubtitle: 'Erleben Sie den Unterschied mit unserem professionellen Taxi-Service',
      benefits: {
        licensedInsured: {
          title: 'Lizenziert & Versichert',
          description: 'Alle unsere Fahrer sind lizenzierte Profis mit umfassendem Versicherungsschutz für Ihre Sicherheit.'
        },
        fixedPricing: {
          title: 'Festpreise',
          description: 'Keine Überraschungen! Unsere transparente Preisgestaltung bedeutet, dass Sie genau wissen, was Sie bezahlen, bevor Sie buchen.'
        },
        availability: {
          title: '24/7 Verfügbarkeit',
          description: 'Tag und Nacht sind wir für Sie da. Buchen Sie jederzeit, überall auf der Insel.'
        },
        multiLanguage: {
          title: 'Mehrsprachiger Support',
          description: 'Unsere Fahrer sprechen Englisch, Thai und mehrere andere Sprachen, um Sie besser zu bedienen.'
        },
        professionalDrivers: {
          title: 'Professionelle Fahrer',
          description: 'Erfahrene, höfliche Fahrer, die jede Ecke von Koh Phangan kennen.'
        },
        easyBooking: {
          title: 'Einfache Buchung',
          description: 'Buchen Sie in Sekunden über unser einfaches Buchungssystem. Kein Konto erforderlich.'
        }
      }
    },
    routePriceList: {
      pageTitle: 'Routenpreise | Koh Phangan',
      prices: 'Routenpreisliste',
      pricingNotice: 'Festpreise pro Route. Keine versteckten Gebühren. In THB.',
      currencyNote: 'Alle Preise in THB.',
      serviceType: 'Servicetyp',
      allServices: 'Alle Services',
      allRoutes: 'Alle Routen',
      popular: 'Beliebt',
      popularRoutes: 'Beliebte Routen',
      popularRoutesSubtitle: 'Die meist genutzten Routen',
      bookNow: 'Route buchen',
      noRoutesFound: 'Keine Routen',
      noRoutesMessage: 'Passen Sie Filter an oder sehen Sie alle Routen',
      viewAllRoutes: 'Alle Routen',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'Insel-Taxi',
      sharedShuttle: 'Sammel-Shuttle',
      privateTransfer: 'Privat-Transfer',
      vipLuxury: 'VIP Luxus',
      airportTransfer: 'Flughafen',
      cargoTaxi: 'Fracht-Taxi',
      bikeTaxi: 'Motorrad-Taxi',
      taxiBoat: 'Taxi-Boot',
      deliveryService: 'Lieferdienst',
      paymentDisclaimer: 'Wichtige Zahlungsinformationen',
      currencyFootnote: 'Alle Transaktionen in THB. Andere Währungen sind Schätzungen.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Jetzt buchen',
      bookPrivateRide: 'Privat buchen',
      bookBoat: 'Boot buchen',
      bookCargoTaxi: 'Fracht buchen',
      bookDelivery: 'Lieferung buchen',
      cargoTaxiTitle: 'Fracht-Taxi Service',
      cargoTaxiDescription: 'Transport für große Gegenstände inselweit.',
      deliveryServiceTitle: 'Insel-Lieferdienst',
      deliveryServiceDescription: 'Schnelle Lieferung auf Phangan. Festpreise pro Zone.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'Sammeltaxi',
        privateTaxi: 'Privattaxi',
        vipTaxi: 'VIP Taxi',
        cargoTaxi: 'Fracht-Taxi',
        taxiBoat: 'Taxi-Boot',
        delivery: 'Lieferung',
        
        perPerson: 'pro Person',
        perVehicle: 'pro Fahrzeug',
        perTrip: 'pro Fahrt',
        
        duration_15_20: '15–20 Min',
        duration_20_30: '20–30 Min',
        duration_30_40: '30–40 Min',
        duration_40_50: '40–50 Min',
        duration_50_60: '50–60 Min',
        
        passengers_1: '1 Passagier',
        passengers_2: '2 Passagiere',
        passengers_4: '4 Passagiere',
        passengers_6: '6 Passagiere',
        passengers_1_4: '1–4 Passagiere',
        passengers_1_6: '1–6 Passagiere',
        passengers_4_8: '4–8 Passagiere'
      }
    },
    zones: {
      'Thong Sala': 'Thong Sala',
      'Baan Tai': 'Baan Tai',
      'Baan Kai': 'Baan Kai',
      'Haad Rin': 'Haad Rin',
      'Srithanu': 'Srithanu',
      'Haad Yao': 'Haad Yao',
      'Haad Salad': 'Haad Salad',
      'Mae Haad': 'Mae Haad',
      'Chaloklum': 'Chaloklum',
      'Bottle Beach': 'Bottle Beach',
      'Thong Nai Pan': 'Thong Nai Pan',
      'Amsterdam Bar': 'Amsterdam Bar'
    }
  },

  fr: {
    common: {
      heroTitle: "Votre Transport Fiable à Koh Phangan",
      heroSubtitle: "Sûr, confortable et toujours à l'heure. Réservez votre taxi en quelques secondes.",
      bookNowButton: "Réserver un Taxi",
      viewPrices: "Voir les Prix"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Prix",
      services: "Services",
      contact: "Contact"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Détails du voyage',
        pickupLabel: 'Lieu de prise en charge',
        pickupPlaceholder: 'Entrez le lieu de prise en charge',
        destinationLabel: 'Destination',
        destinationPlaceholder: 'Où allez-vous?',
        dateLabel: 'Date',
        datePlaceholder: 'Sélectionnez la date',
        timeLabel: 'Heure',
        passengersLabel: 'Nombre de passagers',
        passengersPlaceholder: 'Entrez le nombre de passagers (1-10)',
        passengersText: 'passagers',
        passengerText: 'passager',
        largeGroupNotice: 'Grand groupe - Service de van recommandé',
        contactLabel: 'Informations de contact',
        reviewMessage: 'Veuillez vérifier les détails de votre réservation avant de confirmer. Notre équipe vous contactera sous peu pour confirmer votre trajet.',
        scheduledBooking: 'Planifier pour plus tard',
        scheduledDescription: 'Choisir date et heure',
        immediateBooking: 'Commander maintenant',
        immediateDescription: 'Véhicule disponible le plus proche',
        immediateNoticeTitle: 'Demande de ramassage immédiat',
        immediateNoticeMessage: 'Le chauffeur disponible le plus proche sera notifié immédiatement. Temps de ramassage typique: 5-15 minutes.',
        estimatedPickup: 'Ramassage estimé',
        asapPickup: 'Dès que possible (5-15 minutes)',
        confirmButton: 'Confirmer la réservation',
        fillAllFields: 'Veuillez remplir tous les champs obligatoires',
        confirmHelper: 'En confirmant, vous acceptez nos conditions générales'
      },
      confirmation: {
        title: 'Réservation confirmée!',
        message: 'Votre transfert a été réservé avec succès. Vous recevrez une confirmation sous peu.',
        immediateMessage: 'Votre demande de transfert immédiat a été envoyée. Un chauffeur proche vous contactera sous peu!',
        closeButton: 'Terminé'
      }
    },

    // Services page translations
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'Capacité',
      vehicleType: 'Type de véhicule',
      availability: 'Disponibilité',
      startingAt: 'À partir de',
      bookNow: 'Réserver',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Package tracking',
          'Secure handling',
          'Island-wide coverage'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },

    // Why Choose Us section translations
    whyChooseUs: {
      sectionTitle: 'Pourquoi nous choisir',
      sectionSubtitle: 'Découvrez la différence avec notre service de taxi professionnel',
      benefits: {
        licensedInsured: {
          title: 'Licencié et Assuré',
          description: 'Tous nos chauffeurs sont des professionnels licenciés avec une couverture d\'assurance complète pour votre tranquillité d\'esprit.'
        },
        fixedPricing: {
          title: 'Prix fixes',
          description: 'Pas de surprises ! Notre tarification transparente signifie que vous savez exactement ce que vous paierez avant de réserver.'
        },
        availability: {
          title: 'Disponibilité 24/7',
          description: 'Jour et nuit, nous sommes là pour vous. Réservez à tout moment, n\'importe où sur l\'île.'
        },
        multiLanguage: {
          title: 'Support multilingue',
          description: 'Nos chauffeurs parlent anglais, thaï et plusieurs autres langues pour mieux vous servir.'
        },
        professionalDrivers: {
          title: 'Chauffeurs professionnels',
          description: 'Des chauffeurs expérimentés et courtois qui connaissent chaque coin de Koh Phangan.'
        },
        easyBooking: {
          title: 'Réservation facile',
          description: 'Réservez en quelques secondes grâce à notre système de réservation simple. Aucun compte requis.'
        }
      }
    },

    // Route price list translations
    routePriceList: {
      pageTitle: 'Prix des itinéraires | Koh Phangan',
      prices: 'Liste des prix',
      pricingNotice: 'Prix fixes par trajet. Pas de frais cachés. Prix en THB.',
      currencyNote: 'Tous les prix en THB.',
      serviceType: 'Type de service',
      allServices: 'Tous services',
      allRoutes: 'Tous trajets',
      popular: 'Populaire',
      popularRoutes: 'Itinéraires populaires',
      popularRoutesSubtitle: 'Les trajets les plus empruntés',
      bookNow: 'Réserver ce trajet',
      noRoutesFound: 'Aucun itinéraire',
      noRoutesMessage: 'Ajustez filtres ou parcourez tous les trajets',
      viewAllRoutes: 'Tous trajets',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'Taxi île',
      sharedShuttle: 'Navette partagée',
      privateTransfer: 'Transfert privé',
      vipLuxury: 'VIP Luxe',
      airportTransfer: 'Aéroport',
      cargoTaxi: 'Taxi cargo',
      bikeTaxi: 'Taxi moto',
      taxiBoat: 'Bateau-taxi',
      deliveryService: 'Livraison',
      paymentDisclaimer: 'Informations de paiement importantes',
      currencyFootnote: 'Toutes les transactions en THB. Autres devises estimatives.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Réserver',
      bookPrivateRide: 'Réserver privé',
      bookBoat: 'Réserver bateau',
      bookCargoTaxi: 'Réserver cargo',
      bookDelivery: 'Réserver livraison',
      cargoTaxiTitle: 'Service taxi cargo',
      cargoTaxiDescription: 'Transport gros objets dans toute l\'île.',
      deliveryServiceTitle: 'Livraison sur l\'île',
      deliveryServiceDescription: 'Livraison rapide à Phangan. Tarifs fixes par zone.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'Taxi partagé',
        privateTaxi: 'Taxi privé',
        vipTaxi: 'Taxi VIP',
        cargoTaxi: 'Taxi cargo',
        taxiBoat: 'Bateau-taxi',
        delivery: 'Livraison',
        
        perPerson: 'par personne',
        perVehicle: 'par véhicule',
        perTrip: 'par trajet',
        
        duration_15_20: '15–20 min',
        duration_20_30: '20–30 min',
        duration_30_40: '30–40 min',
        duration_40_50: '40–50 min',
        duration_50_60: '50–60 min',
        
        passengers_1: '1 passager',
        passengers_2: '2 passagers',
        passengers_4: '4 passagers',
        passengers_6: '6 passagers',
        passengers_1_4: '1–4 passagers',
        passengers_1_6: '1–6 passagers',
        passengers_4_8: '4–8 passagers'
      }
    },

    // Zones translations
    zones: {
      'Thong Sala': 'Thong Sala',
      'Baan Tai': 'Baan Tai',
      'Baan Kai': 'Baan Kai',
      'Haad Rin': 'Haad Rin',
      'Srithanu': 'Srithanu',
      'Haad Yao': 'Haad Yao',
      'Haad Salad': 'Haad Salad',
      'Mae Haad': 'Mae Haad',
      'Chaloklum': 'Chaloklum',
      'Bottle Beach': 'Bottle Beach',
      'Thong Nai Pan': 'Thong Nai Pan',
      'Amsterdam Bar': 'Amsterdam Bar'
    }
  },

  zh: {
    common: {
      heroTitle: "您在帕岸岛的可靠出行伙伴",
      heroSubtitle: "安全、舒适、准时。几秒钟内预订您的岛屿出租车。",
      bookNowButton: "立即预订",
      viewPrices: "查看价格"
    },
    navigation: {
      home: "Order Taxi",
      prices: "价格",
      services: "服务",
      contact: "联系"
    },
    booking: {
      form: {
        tripDetailsTitle: '行程详情',
        pickupLabel: '上车地点',
        pickupPlaceholder: '输入上车地点',
        destinationLabel: '目的地',
        destinationPlaceholder: '您要去哪里?',
        dateLabel: '日期',
        datePlaceholder: '选择日期',
        timeLabel: '时间',
        passengersLabel: '乘客人数',
        passengersPlaceholder: '输入乘客人数 (1-10)',
        passengersText: '位乘客',
        passengerText: '位乘客',
        largeGroupNotice: '大团体 - 推荐使用厢型车服务',
        contactLabel: '联系信息',
        reviewMessage: '请在确认前检查您的预订详情。我们的团队将很快与您联系以确认您的行程。',
        scheduledBooking: '稍后安排',
        scheduledDescription: '选择日期和时间',
        immediateBooking: '立即下单',
        immediateDescription: '最近的可用车辆',
        immediateNoticeTitle: '即时接送请求',
        immediateNoticeMessage: '最近的可用司机将立即收到通知。典型接送时间：5-15分钟。',
        estimatedPickup: '预计接送',
        asapPickup: '尽快 (5-15分钟)',
        confirmButton: '确认预订',
        fillAllFields: '请填写所有必填字段',
        confirmHelper: '确认即表示您同意我们的条款和条件'
      },
      confirmation: {
        title: '预订已确认！',
        message: '您的转账已成功预订。您将很快收到确认。',
        immediateMessage: '您的即时转账请求已发送。附近的司机将很快与您联系！',
        closeButton: '完成'
      }
    },

    // Services page translations
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: '容量',
      vehicleType: '车辆类型',
      availability: '可用性',
      startingAt: '起价',
      bookNow: '立即预订',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Package tracking',
          'Secure handling',
          'Island-wide coverage'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },

    // Why Choose Us section translations
    whyChooseUs: {
      sectionTitle: '为什么选择我们',
      sectionSubtitle: '体验我们专业出租车服务的不同之处',
      benefits: {
        licensedInsured: {
          title: '持证并投保',
          description: '我们所有的司机都是持证专业人士，拥有全面的保险保障，让您安心无忧。'
        },
        fixedPricing: {
          title: '固定价格',
          description: '没有惊喜！我们透明的定价意味着您在预订前就知道确切的费用。'
        },
        availability: {
          title: '全天候服务',
          description: '无论白天还是黑夜，我们都为您服务。随时随地在岛上预订。'
        },
        multiLanguage: {
          title: '多语言支持',
          description: '我们的司机会说英语、泰语和其他几种语言，为您提供更好的服务。'
        },
        professionalDrivers: {
          title: '专业司机',
          description: '经验丰富、礼貌周到的司机，熟悉帕岸岛的每一个角落。'
        },
        easyBooking: {
          title: '便捷预订',
          description: '通过我们简单的预订系统，几秒钟即可完成预订。无需注册账户。'
        }
      }
    },

    // Route price list translations
    routePriceList: {
      pageTitle: '路线价格 | 帕岸岛',
      prices: '路线价格表',
      pricingNotice: '路线固定价。无隐藏费用。泰铢计价。',
      currencyNote: '所有价格均为泰铢。',
      serviceType: '服务类型',
      allServices: '所有服务',
      allRoutes: '所有路线',
      popular: '热门',
      popularRoutes: '热门路线',
      popularRoutesSubtitle: '最受欢迎的路线',
      bookNow: '预订此路线',
      noRoutesFound: '未找到路线',
      noRoutesMessage: '请调整筛选或查看所有路线',
      viewAllRoutes: '查看全部',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: '岛屿出租车',
      sharedShuttle: '共享班车',
      privateTransfer: '专车接送',
      vipLuxury: 'VIP豪华',
      airportTransfer: '机场接送',
      cargoTaxi: '货运出租车',
      bikeTaxi: '摩托出租',
      taxiBoat: '出租船',
      deliveryService: '配送服务',
      paymentDisclaimer: '重要付款信息',
      currencyFootnote: '所有交易以泰铢结算。其他货币为估算价。',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: '立即预订',
      bookPrivateRide: '预订专车',
      bookBoat: '预订船只',
      bookCargoTaxi: '预订货运',
      bookDelivery: '预订配送',
      cargoTaxiTitle: '货运出租车',
      cargoTaxiDescription: '岛内大型物品运输。',
      deliveryServiceTitle: '岛屿配送服务',
      deliveryServiceDescription: '帕岸岛快递。分区固定价。',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: '拼车',
        privateTaxi: '专车',
        vipTaxi: 'VIP专车',
        cargoTaxi: '货运',
        taxiBoat: '出租船',
        delivery: '配送',
        
        perPerson: '每人',
        perVehicle: '每车',
        perTrip: '每程',
        
        duration_15_20: '15–20分钟',
        duration_20_30: '20–30分钟',
        duration_30_40: '30–40分钟',
        duration_40_50: '40–50分钟',
        duration_50_60: '50–60分钟',
        
        passengers_1: '1位',
        passengers_2: '2位',
        passengers_4: '4位',
        passengers_6: '6位',
        passengers_1_4: '1–4位',
        passengers_1_6: '1–6位',
        passengers_4_8: '4–8位'
      }
    },

    // Zones translations
    zones: {
      'Thong Sala': '通沙拉',
      'Baan Tai': '班泰',
      'Baan Kai': '班凯',
      'Haad Rin': '哈林',
      'Srithanu': '斯里塔努',
      'Haad Yao': '哈尧',
      'Haad Salad': '哈沙拉',
      'Mae Haad': '美哈',
      'Chaloklum': '查洛克伦',
      'Bottle Beach': '瓶子海滩',
      'Thong Nai Pan': '通奈盘',
      'Amsterdam Bar': '阿姆斯特丹酒吧'
    }
  },

  es: {
    common: {
      heroTitle: "Tu Viaje Confiable en Koh Phangan",
      heroSubtitle: "Seguro, cómodo y siempre puntual. Reserva tu taxi en segundos.",
      bookNowButton: "Reservar Taxi",
      viewPrices: "Ver Precios"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Precios",
      services: "Servicios",
      contact: "Contacto"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Detalles del viaje',
        pickupLabel: 'Lugar de recogida',
        pickupPlaceholder: 'Ingrese el lugar de recogida',
        destinationLabel: 'Destino',
        destinationPlaceholder: '¿A dónde vas?',
        dateLabel: 'Fecha',
        datePlaceholder: 'Seleccione fecha',
        timeLabel: 'Hora',
        passengersLabel: 'Número de pasajeros',
        passengersPlaceholder: 'Ingrese número de pasajeros (1-10)',
        passengersText: 'pasajeros',
        passengerText: 'pasajero',
        largeGroupNotice: 'Gran grupo - Recomendamos autobús',
        contactLabel: 'Información de contacto',
        reviewMessage: 'Por favor revise los detalles de su reserva antes de confirmar. Nuestro equipo se pondrá en contacto con usted en breve para confirmar su viaje.',
        scheduledBooking: 'Programar para más tarde',
        scheduledDescription: 'Elegir fecha y hora',
        immediateBooking: 'Pedir ahora',
        immediateDescription: 'Vehículo disponible más cercano',
        immediateNoticeTitle: 'Solicitud de recogida inmediata',
        immediateNoticeMessage: 'El conductor disponible más cercano será notificado inmediatamente. Tiempo típico de recogida: 5-15 minutos.',
        estimatedPickup: 'Recogida estimada',
        asapPickup: 'Lo antes posible (5-15 minutos)',
        confirmButton: 'Confirmar reserva',
        fillAllFields: 'Por favor complete todos los campos requeridos',
        confirmHelper: 'Al confirmar, acepta nuestros términos y condiciones'
      },
      confirmation: {
        title: '¡Reserva confirmada!',
        message: 'Su transferencia ha sido reservada con éxito. Recibirá una confirmación en breve.',
        immediateMessage: '¡Su solicitud de transferencia inmediata ha sido enviada. Un conductor cercano lo contactará pronto!',
        closeButton: 'Listo'
      }
    },

    // Services page translations
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'Capacidad',
      vehicleType: 'Tipo de vehículo',
      availability: 'Disponibilidad',
      startingAt: 'Desde',
      bookNow: 'Reservar ahora',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Package tracking',
          'Secure handling',
          'Island-wide coverage'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },

    // Why Choose Us section translations
    whyChooseUs: {
      sectionTitle: 'Por qué elegirnos',
      sectionSubtitle: 'Experimenta la diferencia con nuestro servicio de taxi profesional',
      benefits: {
        licensedInsured: {
          title: 'Licenciado y Asegurado',
          description: 'Todos nuestros conductores son profesionales licenciados con cobertura de seguro completa para su tranquilidad.'
        },
        fixedPricing: {
          title: 'Precios fijos',
          description: '¡Sin sorpresas! Nuestra tarificación transparente significa que sabes exactamente lo que pagarás antes de reservar.'
        },
        availability: {
          title: 'Disponibilidad 24/7',
          description: 'De día o de noche, estamos aquí para ti. Reserva en cualquier momento, en cualquier lugar de la isla.'
        },
        multiLanguage: {
          title: 'Soporte multilingüe',
          description: 'Nuestros conductores hablan inglés, tailandés y varios otros idiomas para servirte mejor.'
        },
        professionalDrivers: {
          title: 'Conductores profesionales',
          description: 'Conductores experimentados y corteses que conocen cada rincón de Koh Phangan.'
        },
        easyBooking: {
          title: 'Reserva fácil',
          description: 'Reserva en segundos a través de nuestro simple sistema de reservas. No se requiere cuenta.'
        }
      }
    },

    // Route price list translations
    routePriceList: {
      pageTitle: 'Precios de rutas | Koh Phangan',
      prices: 'Lista de precios',
      pricingNotice: 'Precios fijos por ruta. Sin cargos ocultos. En THB.',
      currencyNote: 'Todos los precios en THB.',
      serviceType: 'Tipo de servicio',
      allServices: 'Todos servicios',
      allRoutes: 'Todas rutas',
      popular: 'Popular',
      popularRoutes: 'Rutas populares',
      popularRoutesSubtitle: 'Las rutas más utilizadas',
      bookNow: 'Reservar esta ruta',
      noRoutesFound: 'No hay rutas',
      noRoutesMessage: 'Ajusta filtros o ve todas las rutas disponibles',
      viewAllRoutes: 'Ver todas',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'Taxi isla',
      sharedShuttle: 'Shuttle compartido',
      privateTransfer: 'Transfer privado',
      vipLuxury: 'VIP Lujo',
      airportTransfer: 'Aeropuerto',
      cargoTaxi: 'Taxi carga',
      bikeTaxi: 'Taxi moto',
      taxiBoat: 'Barco taxi',
      deliveryService: 'Entrega',
      paymentDisclaimer: 'Información de pago importante',
      currencyFootnote: 'Todas las transacciones en THB. Otras monedas son estimadas.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Reservar',
      bookPrivateRide: 'Reservar privado',
      bookBoat: 'Reservar barco',
      bookCargoTaxi: 'Reservar carga',
      bookDelivery: 'Reservar entrega',
      cargoTaxiTitle: 'Servicio taxi carga',
      cargoTaxiDescription: 'Transporte de artículos grandes en toda la isla.',
      deliveryServiceTitle: 'Servicio entrega isla',
      deliveryServiceDescription: 'Entrega rápida en Phangan. Tarifas fijas por zona.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'Taxi compartido',
        privateTaxi: 'Taxi privado',
        vipTaxi: 'Taxi VIP',
        cargoTaxi: 'Taxi de carga',
        taxiBoat: 'Barco taxi',
        delivery: 'Entrega',
        
        perPerson: 'por persona',
        perVehicle: 'por vehículo',
        perTrip: 'por viaje',
        
        duration_15_20: '15–20 min',
        duration_20_30: '20–30 min',
        duration_30_40: '30–40 min',
        duration_40_50: '40–50 min',
        duration_50_60: '50–60 min',
        
        passengers_1: '1 pasajero',
        passengers_2: '2 pasajeros',
        passengers_4: '4 pasajeros',
        passengers_6: '6 pasajeros',
        passengers_1_4: '1–4 pasajeros',
        passengers_1_6: '1–6 pasajeros',
        passengers_4_8: '4–8 pasajeros'
      }
    },

    // Zones translations
    zones: {
      'Thong Sala': 'Thong Sala',
      'Baan Tai': 'Baan Tai',
      'Baan Kai': 'Baan Kai',
      'Haad Rin': 'Haad Rin',
      'Srithanu': 'Srithanu',
      'Haad Yao': 'Haad Yao',
      'Haad Salad': 'Haad Salad',
      'Mae Haad': 'Mae Haad',
      'Chaloklum': 'Chaloklum',
      'Bottle Beach': 'Bottle Beach',
      'Thong Nai Pan': 'Thong Nai Pan',
      'Amsterdam Bar': 'Amsterdam Bar'
    }
  },

  it: {
    common: {
      heroTitle: "Il Tuo Taxi Affidabile a Koh Phangan",
      heroSubtitle: "Sicuro, confortevole e sempre puntuale. Prenota il tuo taxi in pochi secondi.",
      bookNowButton: "Prenota Taxi",
      viewPrices: "Vedi Prezzi"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Prezzi",
      services: "Servizi",
      contact: "Contatto"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Dettagli del viaggio',
        pickupLabel: 'Luogo di ritiro',
        pickupPlaceholder: 'Inserisci il luogo di ritiro',
        destinationLabel: 'Destinazione',
        destinationPlaceholder: 'Dove vai?',
        dateLabel: 'Data',
        datePlaceholder: 'Seleziona data',
        timeLabel: 'Ora',
        passengersLabel: 'Numero di passeggeri',
        passengersPlaceholder: 'Inserisci numero di passeggeri (1-10)',
        passengersText: 'passeggeri',
        passengerText: 'passeggero',
        largeGroupNotice: 'Gruppo grande - Consigliamo il servizio di van',
        contactLabel: 'Informazioni di contatto',
        reviewMessage: 'Si prega di rivedere i dettagli della prenotazione prima di confermare. Il nostro team ti contatterà a breve per confermare il tuo viaggio.',
        scheduledBooking: 'Programma per dopo',
        scheduledDescription: 'Scegli data e ora',
        immediateBooking: 'Ordina ora',
        immediateDescription: 'Veicolo disponibile più vicino',
        immediateNoticeTitle: 'Richiesta di ritiro immediato',
        immediateNoticeMessage: 'L\'autista disponibile più vicino sarà notificato immediatamente. Tempo tipico di ritiro: 5-15 minuti.',
        estimatedPickup: 'Ritiro stimato',
        asapPickup: 'Il prima possibile (5-15 minuti)',
        confirmButton: 'Conferma prenotazione',
        fillAllFields: 'Si prega di compilare tutti i campi obbligatori',
        confirmHelper: 'Confermando, accetti i nostri termini e condizioni'
      },
      confirmation: {
        title: 'Prenotazione confermata!',
        message: 'Il tuo trasferimento è stato prenotato con successo. Riceverai una conferma a breve.',
        immediateMessage: 'La tua richiesta di trasferimento immediato è stata inviata. Un autista vicino ti contatterà presto!',
        closeButton: 'Fatto'
      }
    },
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'Capacità',
      vehicleType: 'Tipo di veicolo',
      availability: 'Disponibilità',
      startingAt: 'A partire da',
      bookNow: 'Prenota ora',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Package tracking',
          'Secure handling',
          'Island-wide coverage'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },
    whyChooseUs: {
      sectionTitle: 'Perché sceglierci',
      sectionSubtitle: 'Scopri la differenza con il nostro servizio taxi professionale',
      benefits: {
        licensedInsured: {
          title: 'Licenziato e Assicurato',
          description: 'Tutti i nostri autisti sono professionisti autorizzati con copertura assicurativa completa per la tua tranquillità.'
        },
        fixedPricing: {
          title: 'Prezzi fissi',
          description: 'Nessuna sorpresa! La nostra tariffazione trasparente significa che sai esattamente cosa pagherai prima di prenotare.'
        },
        availability: {
          title: 'Disponibilità 24/7',
          description: 'Giorno o notte, siamo qui per te. Prenota in qualsiasi momento, ovunque sull\'isola.'
        },
        multiLanguage: {
          title: 'Supporto multilingue',
          description: 'I nostri autisti parlano inglese, thailandese e diverse altre lingue per servirti meglio.'
        },
        professionalDrivers: {
          title: 'Autisti professionali',
          description: 'Autisti esperti e cortesi che conoscono ogni angolo di Koh Phangan.'
        },
        easyBooking: {
          title: 'Prenotazione facile',
          description: 'Prenota in pochi secondi attraverso il nostro semplice sistema di prenotazione. Nessun account richiesto.'
        }
      }
    },
    routePriceList: {
      pageTitle: 'Prezzi percorsi | Koh Phangan',
      prices: 'Listino prezzi',
      pricingNotice: 'Prezzi fissi per percorso. Nessun costo nascosto. In THB.',
      currencyNote: 'Tutti i prezzi in THB.',
      serviceType: 'Tipo servizio',
      allServices: 'Tutti servizi',
      allRoutes: 'Tutti percorsi',
      popular: 'Popolare',
      popularRoutes: 'Percorsi popolari',
      popularRoutesSubtitle: 'I percorsi più utilizzati',
      bookNow: 'Prenota questo percorso',
      noRoutesFound: 'Nessun percorso',
      noRoutesMessage: 'Modifica filtri o vedi tutti i percorsi',
      viewAllRoutes: 'Tutti percorsi',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'Taxi isola',
      sharedShuttle: 'Shuttle condiviso',
      privateTransfer: 'Transfer privato',
      vipLuxury: 'VIP Lusso',
      airportTransfer: 'Aeroporto',
      cargoTaxi: 'Taxi cargo',
      bikeTaxi: 'Taxi moto',
      taxiBoat: 'Barca taxi',
      deliveryService: 'Consegna',
      paymentDisclaimer: 'Informazioni pagamento importanti',
      currencyFootnote: 'Tutte le transazioni in THB. Altre valute sono stime.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Prenota',
      bookPrivateRide: 'Prenota privato',
      bookBoat: 'Prenota barca',
      bookCargoTaxi: 'Prenota cargo',
      bookDelivery: 'Prenota consegna',
      cargoTaxiTitle: 'Servizio taxi cargo',
      cargoTaxiDescription: 'Trasporto oggetti grandi in tutta l\'isola.',
      deliveryServiceTitle: 'Servizio consegna isola',
      deliveryServiceDescription: 'Consegna veloce a Phangan. Tariffe fisse per zona.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'Taxi condiviso',
        privateTaxi: 'Taxi privato',
        vipTaxi: 'Taxi VIP',
        cargoTaxi: 'Taxi cargo',
        taxiBoat: 'Barca taxi',
        delivery: 'Consegna',
        
        perPerson: 'a persona',
        perVehicle: 'a veicolo',
        perTrip: 'a viaggio',
        
        duration_15_20: '15–20 min',
        duration_20_30: '20–30 min',
        duration_30_40: '30–40 min',
        duration_40_50: '40–50 min',
        duration_50_60: '50–60 min',
        
        passengers_1: '1 passeggero',
        passengers_2: '2 passeggeri',
        passengers_4: '4 passeggeri',
        passengers_6: '6 passeggeri',
        passengers_1_4: '1–4 passeggeri',
        passengers_1_6: '1–6 passeggeri',
        passengers_4_8: '4–8 passeggeri'
      }
    },
    zones: {
      'Thong Sala': 'Thong Sala',
      'Baan Tai': 'Baan Tai',
      'Baan Kai': 'Baan Kai',
      'Haad Rin': 'Haad Rin',
      'Srithanu': 'Srithanu',
      'Haad Yao': 'Haad Yao',
      'Haad Salad': 'Haad Salad',
      'Mae Haad': 'Mae Haad',
      'Chaloklum': 'Chaloklum',
      'Bottle Beach': 'Bottle Beach',
      'Thong Nai Pan': 'Thong Nai Pan',
      'Amsterdam Bar': 'Amsterdam Bar'
    }
  },

  nl: {
    common: {
      heroTitle: "Uw Betrouwbare Rit op Koh Phangan",
      heroSubtitle: "Veilig, comfortabel en altijd op tijd. Boek uw taxi in enkele seconden.",
      bookNowButton: "Boek Nu",
      viewPrices: "Bekijk Prijzen"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Prijzen",
      services: "Diensten",
      contact: "Contact"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Reisdetails',
        pickupLabel: 'Ophaallocatie',
        pickupPlaceholder: 'Voer ophaallocatie in',
        destinationLabel: 'Bestemming',
        destinationPlaceholder: 'Waar gaat u naartoe?',
        dateLabel: 'Datum',
        datePlaceholder: 'Selecteer datum',
        timeLabel: 'Tijd',
        passengersLabel: 'Aantal passagiers',
        passengersPlaceholder: 'Voer aantal passagiers (1-10) in',
        passengersText: 'passagiers',
        passengerText: 'passagier',
        largeGroupNotice: 'Grote groep - Vraag een bus aan',
        contactLabel: 'Contactgegevens',
        reviewMessage: 'Controleer uw boekingsgegevens voordat u bevestigt. Ons team neemt binnenkort contact met u op om uw rit te bevestigen.',
        scheduledBooking: 'Later plannen',
        scheduledDescription: 'Kies datum en tijd',
        immediateBooking: 'Nu bestellen',
        immediateDescription: 'Dichtstbijzijnde beschikbare voertuig',
        immediateNoticeTitle: 'Onmiddellijke ophaalverzoek',
        immediateNoticeMessage: 'De dichtstbijzijnde beschikbare chauffeur wordt onmiddellijk op de hoogte gebracht. Typische ophaaltijd: 5-15 minuten.',
        estimatedPickup: 'Geschatte ophaling',
        asapPickup: 'Zo snel mogelijk (5-15 minuten)',
        confirmButton: 'Boeking bevestigen',
        fillAllFields: 'Vul alle verplichte velden in',
        confirmHelper: 'Door te bevestigen gaat u akkoord met onze voorwaarden'
      },
      confirmation: {
        title: 'Boeking bevestigd!',
        message: 'Uw transfer is succesvol geboekt. U ontvangt binnenkort een bevestiging.',
        immediateMessage: 'Uw onmiddellijke transferverzoek is verzonden. Een nabijgelegen chauffeur neemt binnenkort contact met u op!',
        closeButton: 'Klaar'
      }
    },
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'Capaciteit',
      vehicleType: 'Voertuigtype',
      availability: 'Beschikbaarheid',
      startingAt: 'Vanaf',
      bookNow: 'Boek nu',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Package tracking',
          'Secure handling',
          'Island-wide coverage'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },
    whyChooseUs: {
      sectionTitle: 'Waarom voor ons kiezen',
      sectionSubtitle: 'Ervaar het verschil met onze professionele taxiservice',
      benefits: {
        licensedInsured: {
          title: 'Gelicentieerd & Verzekerd',
          description: 'Al onze chauffeurs zijn gelicentieerd professionals met uitgebreide verzekeringsdekking voor uw gemoedsrust.'
        },
        fixedPricing: {
          title: 'Vaste prijzen',
          description: 'Geen verrassingen! Onze transparante prijsstelling betekent dat u precies weet wat u betaalt voordat u boekt.'
        },
        availability: {
          title: '24/7 Beschikbaarheid',
          description: 'Dag en nacht zijn we er voor u. Boek altijd, overal op het eiland.'
        },
        multiLanguage: {
          title: 'Meertalige ondersteuning',
          description: 'Onze chauffeurs spreken Engels, Thai en verschillende andere talen om u beter van dienst te zijn.'
        },
        professionalDrivers: {
          title: 'Professionele chauffeurs',
          description: 'Ervaren, hoffelijke chauffeurs die elke hoek van Koh Phangan kennen.'
        },
        easyBooking: {
          title: 'Eenvoudig boeken',
          description: 'Boek in enkele seconden via ons eenvoudige boekingssysteem. Geen account vereist.'
        }
      }
    },
    routePriceList: {
      pageTitle: 'Routeprijzen | Koh Phangan',
      prices: 'Routeprijslijst',
      pricingNotice: 'Vaste prijzen per route. Geen verborgen kosten. In THB.',
      currencyNote: 'Alle prijzen in THB.',
      serviceType: 'Servicetype',
      allServices: 'Alle services',
      allRoutes: 'Alle routes',
      popular: 'Populair',
      popularRoutes: 'Populaire routes',
      popularRoutesSubtitle: 'De meest gebruikte routes',
      bookNow: 'Boek deze route',
      noRoutesFound: 'Geen routes',
      noRoutesMessage: 'Pas filters aan of bekijk alle routes',
      viewAllRoutes: 'Alle routes',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'Eiland taxi',
      sharedShuttle: 'Gedeelde shuttle',
      privateTransfer: 'Privé transfer',
      vipLuxury: 'VIP Luxe',
      airportTransfer: 'Luchthaven',
      cargoTaxi: 'Vracht taxi',
      bikeTaxi: 'Motor taxi',
      taxiBoat: 'Taxi boot',
      deliveryService: 'Bezorging',
      paymentDisclaimer: 'Belangrijke betaalinformatie',
      currencyFootnote: 'Alle transacties in THB. Andere valuta zijn schattingen.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'Boek nu',
      bookPrivateRide: 'Boek privé',
      bookBoat: 'Boek boot',
      bookCargoTaxi: 'Boek vracht',
      bookDelivery: 'Boek bezorging',
      cargoTaxiTitle: 'Vracht taxi service',
      cargoTaxiDescription: 'Transport voor grote items eilandbreed.',
      deliveryServiceTitle: 'Eiland bezorgservice',
      deliveryServiceDescription: 'Snelle bezorging op Phangan. Vaste tarieven per zone.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'Gedeelde taxi',
        privateTaxi: 'Privé taxi',
        vipTaxi: 'VIP taxi',
        cargoTaxi: 'Vracht taxi',
        taxiBoat: 'Taxi boot',
        delivery: 'Bezorging',
        
        perPerson: 'per persoon',
        perVehicle: 'per voertuig',
        perTrip: 'per reis',
        
        duration_15_20: '15–20 min',
        duration_20_30: '20–30 min',
        duration_30_40: '30–40 min',
        duration_40_50: '40–50 min',
        duration_50_60: '50–60 min',
        
        passengers_1: '1 passagier',
        passengers_2: '2 passagiers',
        passengers_4: '4 passagiers',
        passengers_6: '6 passagiers',
        passengers_1_4: '1–4 passagiers',
        passengers_1_6: '1–6 passagiers',
        passengers_4_8: '4–8 passagiers'
      }
    },
    zones: {
      'Thong Sala': 'Thong Sala',
      'Baan Tai': 'Baan Tai',
      'Baan Kai': 'Baan Kai',
      'Haad Rin': 'Haad Rin',
      'Srithanu': 'Srithanu',
      'Haad Yao': 'Haad Yao',
      'Haad Salad': 'Haad Salad',
      'Mae Haad': 'Mae Haad',
      'Chaloklum': 'Chaloklum',
      'Bottle Beach': 'Bottle Beach',
      'Thong Nai Pan': 'Thong Nai Pan',
      'Amsterdam Bar': 'Amsterdam Bar'
    },
    footer: {
      rights: "Alle rechten voorbehouden"
    }
  },

  he: {
    common: {
      heroTitle: "הנסיעה האמינה שלך בקוה פנגן",
      heroSubtitle: "בטוח, נוח ותמיד בזמן. הזמינו את המונית שלכם בשניות.",
      bookNowButton: "הזמן מונית עכשיו",
      viewPrices: "צפה במחירים"
    },
    navigation: {
      home: "Order Taxi",
      prices: "מחירים",
      services: "שירותים",
      contact: "צור קשר"
    },
    booking: {
      form: {
        tripDetailsTitle: 'פרטי הנסיעה',
        pickupLabel: 'מקום איסוף',
        pickupPlaceholder: 'הזן מקום איסוף',
        destinationLabel: 'יעד',
        destinationPlaceholder: 'לאן אתה נוסע?',
        dateLabel: 'תאריך',
        datePlaceholder: 'בחר תאריך',
        timeLabel: 'שעה',
        passengersLabel: 'מספר נוסעים',
        passengersPlaceholder: 'הזן מספר נוסעים (1-10)',
        passengersText: 'נוסעים',
        passengerText: 'נוסע',
        largeGroupNotice: 'קבוצה גדולה - מומלץ שירות מונית',
        contactLabel: 'פרטי קשר',
        reviewMessage: 'אנא בדוק את פרטי ההזמנה שלך לפני האישור. הצוות שלנו ייצור איתך קשר בקרוב כדי לאשר את הנסיעה שלך.',
        scheduledBooking: 'תזמן למועד מאוחר יותר',
        scheduledDescription: 'בחר תאריך ושעה',
        immediateBooking: 'הזמן עכשיו',
        immediateDescription: 'הרכב הזמין הקרוב ביותר',
        immediateNoticeTitle: 'בקשת איסוף מיידית',
        immediateNoticeMessage: 'הנהג הזמין הקרוב ביותר יקבל הודעה מיד. זמן איסוף טיפוסי: 5-15 דקות.',
        estimatedPickup: 'איסוף משוער',
        asapPickup: 'בהקדם האפשרי (5-15 דקות)',
        confirmButton: 'אשר הזמנה',
        fillAllFields: 'אנא מלא את כל השדות הנדרשים',
        confirmHelper: 'באישור, אתה מסכים לתנאים שלנו'
      },
      confirmation: {
        title: 'ההזמנה אושרה!',
        message: 'ההעברה שלך הוזמנה בהצלחה. תקבל אישור בקרוב.',
        immediateMessage: 'בקשת ההעברה המיידית שלך נשלחה. נהג קרוב יצור איתך קשר בקרוב!',
        closeButton: 'סיום'
      }
    },

    // Services page translations
    services: {
      sectionTitle: 'Our Taxi Services',
      sectionIntro: 'Choose the type of taxi service that fits your trip.',
      servicesAvailable: 'services available',
      capacity: 'קיבולת',
      vehicleType: 'סוג רכב',
      availability: 'זמינות',
      startingAt: 'החל מ',
      bookNow: 'הזמן עכשיו',
      stats: {
        title: 'Service Statistics',
        activeVehicles: 'Active vehicles',
        happyCustomers: 'Happy customers',
        islandCoverage: 'Island coverage',
        serviceAvailable: 'Service available',
        drivers: 'Active Drivers',
        trips: 'Daily Trips',
        rating: 'Average Rating',
        coverage: 'Island Coverage'
      },
      filters: {
        allServices: 'All services',
        popular: 'Popular',
        transfers: 'Transfers',
        private: 'Private'
      },
      sharedTaxi: {
        title: 'Shared Taxi',
        description: 'Affordable shared rides between popular spots across Koh Phangan.',
        priceRange: '฿150-300',
        features: [
          'Fixed price per person',
          'Fast pick-up from main locations',
          'Air-conditioned vehicles',
          'Ideal for solo travelers'
        ]
      },
      privateTaxi: {
        title: 'Private Taxi',
        description: 'Your own private driver for fast and comfortable transport anywhere on the island.',
        priceRange: '฿400-800',
        features: [
          'Direct, non-stop route',
          'Clean and comfortable cars',
          'Flexible pick-up and drop-off',
          'Great for families & small groups'
        ]
      },
      islandTaxi: {
        title: 'Island Taxi',
        description: 'Point-to-point transfers across the island, available 24/7.',
        priceRange: '฿200-600',
        features: [
          'Fixed transparent pricing',
          'Local experienced drivers',
          'Quick arrival time',
          'Covers all beaches and villages'
        ]
      },
      airportTransfer: {
        title: 'Airport Transfer',
        description: 'Smooth connection between Koh Phangan and Samui Airport — stress-free travel.',
        priceRange: '฿1,200-2,500',
        features: [
          'Boat + taxi coordination',
          'Guaranteed on-time arrival',
          'Luggage assistance',
          'Perfect for early flights'
        ]
      },
      cargoDelivery: {
        title: 'Cargo Delivery',
        description: 'Fast and safe delivery of parcels, luggage, and personal items across the island.',
        priceRange: '฿150-500',
        features: [
          'Same-day delivery',
          'Package tracking',
          'Secure handling',
          'Island-wide coverage'
        ]
      },
      taxiBoat: {
        title: 'Taxi Boat',
        description: 'Direct boat transfer to remote beaches and bays around Koh Phangan.',
        priceRange: '฿500-2,000',
        features: [
          'Fast access to Haad Yuan, Haad Tien, Bottle Beach',
          'Safe licensed boat operators',
          'Weather-checked departures',
          'Ideal for parties & remote resorts'
        ]
      },
      vipTaxi: {
        title: 'VIP Taxi',
        description: 'Premium vehicles for maximum comfort, privacy, and style.',
        priceRange: '฿1,000-2,000',
        features: [
          'New luxury cars',
          'Professional VIP drivers',
          'Extra luggage space',
          'Priority pick-up'
        ]
      },
      deliveryService: {
        title: 'Delivery Service',
        description: 'Quick delivery of goods, shopping, and essentials anywhere on the island.',
        priceRange: '฿100-400',
        features: [
          'All-island coverage',
          'Fixed low price',
          'Reliable handling',
          'Fast delivery times'
        ]
      },
      // Keep legacy entries for backward compatibility
      pierTransfer: {
        title: 'Pier Transfer',
        description: 'Direct transfers from/to Thong Sala Pier and Haad Rin Pier',
        priceRange: '฿200-600',
        features: [
          'Meet & greet service',
          'Luggage assistance',
          'Ferry schedule tracking',
          'Direct routes'
        ]
      },
      privateRide: {
        title: 'Private Ride',
        description: 'Premium private transportation for a luxury travel experience',
        priceRange: '฿500-1,500',
        features: [
          'Premium vehicles',
          'Dedicated driver',
          'Flexible scheduling',
          'VIP treatment'
        ]
      },
      groupTransfer: {
        title: 'Group Transfer',
        description: 'Spacious minivans perfect for groups and families',
        priceRange: '฿800-2,000',
        features: [
          'Up to 12 passengers',
          'Extra luggage space',
          'Split payment options',
          'Group discounts'
        ]
      },
      bikeTaxi: {
        title: 'Bike Taxi',
        description: 'Quick motorcycle taxi service for solo travelers',
        priceRange: '฿50-300',
        features: [
          'Fastest option',
          'Traffic advantage',
          'Helmet provided',
          'Short distances'
        ]
      }
    },

    // Why Choose Us section translations
    whyChooseUs: {
      sectionTitle: 'למה לבחור בנו',
      sectionSubtitle: 'חווה את ההבדל עם שירות המוניות המקצועי שלנו',
      benefits: {
        licensedInsured: {
          title: 'בעלי רישיון ומבוטחים',
          description: 'כל הנהגים שלנו הם אנשי מקצוע מורשים עם כיסוי ביטוחי מקיף לשקט נפשכם.'
        },
        fixedPricing: {
          title: 'תמחור קבוע',
          description: 'ללא הפתעות! התמחור השקוף שלנו אומר שאתה יודע בדיוק מה תשלם לפני שתזמין.'
        },
        availability: {
          title: 'זמינות 24/7',
          description: 'יום או לילה, אנחנו כאן בשבילכם. הזמינו בכל זמן, בכל מקום באי.'
        },
        multiLanguage: {
          title: 'תמיכה רב לשונית',
          description: 'הנהגים שלנו מדברים אנגלית, תאילנדית ועוד מספר שפות כדי לשרת אתכם טוב יותר.'
        },
        professionalDrivers: {
          title: 'נהגים מקצועיים',
          description: 'נהגים מנוסים ואדיבים שמכירים כל פינה בקוה פנגן.'
        },
        easyBooking: {
          title: 'הזמנה קלה',
          description: 'הזמינו בשניות דרך מערכת ההזמנה הפשוטה שלנו. אין צורך בחשבון.'
        }
      }
    },

    // Route price list translations
    routePriceList: {
      pageTitle: 'מחירון מסלולים | קו פנגן',
      prices: 'מחירון מסלולים',
      pricingNotice: 'מחירים קבועים למסלול. ללא עמלות נסתרות. במטבע THB.',
      currencyNote: 'כל המחירים ב-THB.',
      serviceType: 'סוג שירות',
      allServices: 'כל השירותים',
      allRoutes: 'כל המסלולים',
      popular: 'פופולרי',
      popularRoutes: 'מסלולים פופולריים',
      popularRoutesSubtitle: 'המסלולים הנוסעים ביותר',
      bookNow: 'הזמן מסלול זה',
      noRoutesFound: 'לא נמצאו מסלולים',
      noRoutesMessage: 'התאם סינון או צפה בכל המסלולים',
      viewAllRoutes: 'כל המסלולים',
      searchPlaceholder: 'Search by location...',
      searchLabel: 'Search Routes',
      resultsFound: 'routes found',
      islandTaxi: 'מונית באי',
      sharedShuttle: 'הסעה משותפת',
      privateTransfer: 'הסעה פרטית',
      vipLuxury: 'VIP יוקרה',
      airportTransfer: 'הסעת שדה',
      cargoTaxi: 'מוני משא',
      bikeTaxi: 'מונית אופנוע',
      taxiBoat: 'מונית סירה',
      deliveryService: 'שירות משלוחים',
      paymentDisclaimer: 'מידע חשוב לתשלום',
      currencyFootnote: 'כל העסקאות ב-THB. מטבעות אחרים הם אומדנים.',
      sharedTaxi: 'Shared Taxi',
      perPerson: 'Per Person',
      passengers: 'Passengers',
      bookThisRoute: 'הזמן עכשיו',
      bookPrivateRide: 'הזמן פרטי',
      bookBoat: 'הזמן סירה',
      bookCargoTaxi: 'הזמן משא',
      bookDelivery: 'הזמן משלוח',
      cargoTaxiTitle: 'שירות מונית משא',
      cargoTaxiDescription: 'הובלה לפריטים גדולים בכל הא.',
      deliveryServiceTitle: 'שירות משלוחים באי',
      deliveryServiceDescription: 'משלוח מהיר בפנגן. תעריפים קבועים לפי אזור.',
      
      // NEW: Service metadata translations
      meta: {
        sharedTaxi: 'מונית משותפת',
        privateTaxi: 'מונית פרטית',
        vipTaxi: 'מונית VIP',
        cargoTaxi: 'מוני משא',
        taxiBoat: 'מונית סירה',
        delivery: 'משלוח',
        
        perPerson: 'לאדם',
        perVehicle: 'לרכב',
        perTrip: 'לנסיעה',
        
        duration_15_20: '15–20 דק׳',
        duration_20_30: '20–30 דק׳',
        duration_30_40: '30–40 דק׳',
        duration_40_50: '40–50 דק׳',
        duration_50_60: '50–60 דק׳',
        
        passengers_1: 'נוסע 1',
        passengers_2: '2 נוסעים',
        passengers_4: '4 נוסעים',
        passengers_6: '6 נוסעים',
        passengers_1_4: '1–4 נוסעים',
        passengers_1_6: '1–6 נוסעים',
        passengers_4_8: '4–8 נוסעים'
      }
    },

    // Zones translations
    zones: {
      'Thong Sala': 'תונג סאלה',
      'Baan Tai': 'באן טאי',
      'Baan Kai': 'באן קאי',
      'Haad Rin': 'האד רין',
      'Srithanu': 'סריתאנו',
      'Haad Yao': 'האד יאו',
      'Haad Salad': 'האד סאלאד',
      'Mae Haad': 'מאה האד',
      'Chaloklum': 'צ\'אלוקלום',
      'Bottle Beach': 'חוף הבקבוק',
      'Thong Nai Pan': 'תונג נאי פאן',
      'Amsterdam Bar': 'בר אמסטרדם'
    }
  },

  uk: {
    common: {
      heroTitle: "Ваше Надійне Таксі на Ко Пангані",
      heroSubtitle: "Безпечно, комфортно і завжжі вчасно. Забронюйте таксі за лічені секунди.",
      bookNowButton: "Замовити Таксі",
      viewPrices: "Переглянути Ціни"
    },
    navigation: {
      home: "Order Taxi",
      prices: "Ціни",
      services: "Послуги",
      contact: "Контакти"
    },
    booking: {
      form: {
        tripDetailsTitle: 'Деталі поїздки',
        pickupLabel: 'Місце посадки',
        pickupPlaceholder: 'Введіть місце посадки',
        destinationLabel: 'Пункт призначення',
        destinationPlaceholder: 'Куди їдете?',
        dateLabel: 'Дата',
        datePlaceholder: 'Оберіть дату',
        timeLabel: 'Час',
        passengersLabel: 'Кількість пасажирів',
        passengersPlaceholder: 'Введіть кількість пасажирів (1-10)',
        passengersText: 'пасажирів',
        passengerText: 'пасажир',
        largeGroupNotice: 'Велика група - рекомендуємо мікроавтобус',
        contactLabel: 'Контактна інформація',
        reviewMessage: 'Будь ласка, перевірте деталі бронювання перед підтвердженням. Наша команда зв\'яжеться з вами найближчим часом для підтвердження поїздки.',
        scheduledBooking: 'Запланувати на пізніше',
        scheduledDescription: 'Оберіть дату та час',
        immediateBooking: 'Замовити зараз',
        immediateDescription: 'Найближча доступна машина',
        immediateNoticeTitle: 'Миттєве замовлення',
        immediateNoticeMessage: 'Найближчий доступний водій буде повідомлений негайно. Типовий час прибуття: 5-15 хвилин.',
        estimatedPickup: 'Очікуване прибуття',
        asapPickup: 'Якомога швидше (5-15 хвилин)',
        confirmButton: 'Підтвердити бронювання',
        fillAllFields: 'Будь ласка, заповніть усі обов\'язкові поля',
        confirmHelper: 'Підтверджуючи, ви приймаєте наші умови'
      },
      confirmation: {
        title: 'Бронювання підтверджено!',
        message: 'Ваш трансфер успішно забронований. Ви отримаєте підтвердження найближчим часом.',
        immediateMessage: 'Ваш запит на негайний трансфер надіслано. Найближчий водій зв\'яжеться з вами найближчим часом!',
        closeButton: 'Готово'
      }
    },
    services: {
      sectionTitle: 'Наші послуги таксі',
      sectionIntro: 'Оберіть тип послуги таксі, який підходить для вашої поїздки.',
      servicesAvailable: 'доступно послуг',
      capacity: 'Місткість',
      vehicleType: 'Тип транспорту',
      availability: 'Доступність',
      startingAt: 'Ціна від',
      bookNow: 'Забронювати',
      stats: {
        title: 'Статистика послуг',
        activeVehicles: 'Активних авто',
        happyCustomers: 'Задоволених клієнтів',
        islandCoverage: 'Покриття острова',
        serviceAvailable: 'Послуга доступна',
        drivers: 'Активних водіїв',
        trips: 'Поїздок щодня',
        rating: 'Середній рейтинг',
        coverage: 'Покриття острова'
      },
      filters: {
        allServices: 'Всі послуги',
        popular: 'Популярні',
        transfers: 'Трансфери',
        private: 'Приватні'
      },
      sharedTaxi: {
        title: 'Спільне таксі',
        description: 'Доступні спільні поїздки між популярними місцями на Ко Пангані.',
        priceRange: '฿150-300',
        features: [
          'Фіксована ціна на особу',
          'Швидка посадка з головних локацій',
          'Автомобілі з кондиціонером',
          'Ідеально для самостійних мандрівників'
        ]
      },
      privateTaxi: {
        title: 'Приватне таксі',
        description: 'Ваш власний водій для швидкого та комфортного транспорту будь-де на острові.',
        priceRange: '฿400-800',
        features: [
          'Прямий маршрут без зупинок',
          'Чисті та комфортні автомобілі',
          'Гнучка посадка та висадка',
          'Чудово для сімей та невеликих груп'
        ]
      },
      islandTaxi: {
        title: 'Острівне таксі',
        description: 'Трансфери від точки до точки по всьому острову, доступні 24/7.',
        priceRange: '฿200-600',
        features: [
          'Фіксоване прозоре ціноутворення',
          'Місцеві досвідчені водії',
          'Швидке прибуття',
          'Покриває всі пляжі та села'
        ]
      },
      airportTransfer: {
        title: 'Трансфер до аеропорту',
        description: 'Безперебійне сполучення між Ко Панганом та аеропортом Самуї — подорож без стресу.',
        priceRange: '฿1,200-2,500',
        features: [
          'Координація човна + таксі',
          'Гарантоване прибуття вчасно',
          'Допомога з багажем',
          'Ідеально для ранніх рейсів'
        ]
      },
      cargoDelivery: {
        title: 'Вантажна доставка',
        description: 'Швидка та безпечна доставка посилок, багажу та особистих речей по всьому острову.',
        priceRange: '฿150-500',
        features: [
          'Доставка в той же день',
          'Послуга від дверей до дверей',
          'Надійні водії',
          'Підтвердження в реальному часі'
        ]
      },
      taxiBoat: {
        title: 'Таксі-човен',
        description: 'Прямий трансфер човном до віддалених пляжів та бухт навколо Ко Пангану.',
        priceRange: '฿500-2,000',
        features: [
          'Швидкий доступ до Хаад Юань, Хаад Тієн, Боттл Біч',
          'Безпечні ліцензовані оператори човнів',
          'Відправлення з перевіркою погоди',
          'Ідеально для вечірок та віддалених курортів'
        ]
      },
      vipTaxi: {
        title: 'VIP таксі',
        description: 'Преміум транспорт для максимального комфорту, приватності та стилю.',
        priceRange: '฿1,000-2,000',
        features: [
          'Нові розкішні автомобілі',
          'Професійні VIP водії',
          'Додатковий простір для багажу',
          'Пріоритетна посадка'
        ]
      },
      deliveryService: {
        title: 'Служба доставки',
        description: 'Швидка доставка товарів, покупок та необхідних речей будь-де на острові.',
        priceRange: '฿100-400',
        features: [
          'Покриття всього острова',
          'Фіксована низька ціна',
          'Надійна обробка',
          'Швидкі терміни доставки'
        ]
      },
      pierTransfer: {
        title: 'Трансфер до причалу',
        description: 'Прямі трансфери з/до причалу Тонг Сала та Хаад Рін',
        priceRange: '฿200-600',
        features: [
          'Послуга зустрічі',
          'Допомога з багажем',
          'Відстеження розкладу поромів',
          'Прямі маршрути'
        ]
      },
      privateRide: {
        title: 'Приватна поїздка',
        description: 'Преміум приватний транспорт для розкішної подорожі',
        priceRange: '฿500-1,500',
        features: [
          'Преміум автомобілі',
          'Виділений водій',
          'Гнучке планування',
          'VIP обслуговування'
        ]
      },
      groupTransfer: {
        title: 'Груповий трансфер',
        description: 'Просторі мікроавтобуси ідеально підходять для груп та сімей',
        priceRange: '฿800-2,000',
        features: [
          'До 12 пасажирів',
          'Додатковий простір для багажу',
          'Опції розділеної оплати',
          'Групові знижки'
        ]
      },
      bikeTaxi: {
        title: 'Мото-таксі',
        description: 'Швидка послуга мото-таксі для самостійних мандрівників',
        priceRange: '฿50-300',
        features: [
          'Найшвидший варіант',
          'Перевага в трафіку',
          'Шолом надається',
          'Короткі відстані'
        ]
      }
    },
    whyChooseUs: {
      sectionTitle: 'Чому обирають нас',
      sectionSubtitle: 'Відчуйте різницю з нашим професійним сервісом таксі',
      benefits: {
        licensedInsured: {
          title: 'Ліцензовані та застраховані',
          description: 'Всі наші водії - ліцензовані професіонали з повним страховим покриттям для вашого спокою.'
        },
        fixedPricing: {
          title: 'Фіксовані ціни',
          description: 'Без сюрпризів! Прозоре ціноутворення означає, що ви знаєте точну вартість до бронювання.'
        },
        availability: {
          title: 'Доступні 24/7',
          description: 'День і ніч, ми тут для вас. Бронюйте будь-коли в будь-якій точці острова.'
        },
        multiLanguage: {
          title: 'Багатомовна підтримка',
          description: 'Наші водії розмовляють англійською, тайською та кількома іншими мовами для вашої зручності.'
        },
        professionalDrivers: {
          title: 'Професійні водії',
          description: 'Досвідчені, ввічливі водії, які знають кожен куточок Ко Пангана.'
        },
        easyBooking: {
          title: 'Легке бронювання',
          description: 'Забронюйте за лічені секунди через нашу просту систему бронювання. Реєстрація не потрібна.'
        }
      }
    },
    routePriceList: {
      pageTitle: 'Ціни на маршрути | Ко Панган',
      prices: 'Прайс-лист маршрутів',
      pricingNotice: 'Фіксовані ціни. Без прихованих комісій. Вказані в THB.',
      currencyNote: 'Всі ціни в THB.',
      serviceType: 'Тип послуги',
      allServices: 'Всі послуги',
      allRoutes: 'Всі маршрути',
      popular: 'Популярний',
      popularRoutes: 'Популярні маршрути',
      popularRoutesSubtitle: 'Найпопулярніші напрямки',
      bookNow: 'Забронювати маршрут',
      noRoutesFound: 'Маршрути не знайдені',
      noRoutesMessage: 'Змініть фільтри або перегляньте всі маршрути',
      viewAllRoutes: 'Всі маршрути',
      searchPlaceholder: 'Пошук за локацією...',
      searchLabel: 'Пошук маршрутів',
      resultsFound: 'маршрутів знайдено',
      islandTaxi: 'Таксі по острову',
      sharedShuttle: 'Спільний шатл',
      privateTransfer: 'Приват. трансфер',
      vipLuxury: 'VIP клас',
      airportTransfer: 'До аеропорту',
      cargoTaxi: 'Вантажне таксі',
      bikeTaxi: 'Мото-таксі',
      taxiBoat: 'Таксі-човен',
      deliveryService: 'Доставка',
      paymentDisclaimer: 'Важлива інформація про оплату',
      currencyFootnote: 'Всі платежі в THB. Ціни в інших валютах — орієнтовні.',
      sharedTaxi: 'Спільне таксі',
      perPerson: 'На особу',
      passengers: 'Пасажирів',
      bookThisRoute: 'Забронювати',
      bookPrivateRide: 'Замовити приватну',
      bookBoat: 'Замовити човен',
      bookCargoTaxi: 'Замовити вантажне таксі',
      bookDelivery: 'Замовити доставку',
      cargoTaxiTitle: 'Вантажне таксі',
      cargoTaxiDescription: 'Перевезення великих речей по всьому острову.',
      deliveryServiceTitle: 'Доставка по острову',
      deliveryServiceDescription: 'Швидка доставка по Пангану. Фіксовані ціни.',
      meta: {
        sharedTaxi: 'Спільне таксі',
        privateTaxi: 'Індивідуальне таксі',
        vipTaxi: 'VIP таксі',
        cargoTaxi: 'Вантажне таксі',
        taxiBoat: 'Таксі-човен',
        delivery: 'Доставка',
        perPerson: 'з особи',
        perVehicle: 'за машину',
        perTrip: 'за поїздку',
        duration_15_20: '15–20 хв',
        duration_20_30: '20–30 хв',
        duration_30_40: '30–40 хв',
        duration_40_50: '40–50 хв',
        duration_50_60: '50–60 хв',
        passengers_1: '1 пасажир',
        passengers_2: '2 пасажири',
        passengers_4: '4 пасажири',
        passengers_6: '6 пасажирів',
        passengers_1_4: '1–4 пасажири',
        passengers_1_6: '1–6 пасажирів',
        passengers_4_8: '4–8 пасажирів'
      }
    },
    zones: {
      'Thong Sala': 'Тонг Сала',
      'Baan Tai': 'Баан Тай',
      'Baan Kai': 'Баан Кай',
      'Haad Rin': 'Хаад Рін',
      'Srithanu': 'Шрітану',
      'Haad Yao': 'Хаад Яо',
      'Haad Salad': 'Хаад Салад',
      'Mae Haad': 'Мае Хаад',
      'Chaloklum': 'Чалоклум',
      'Bottle Beach': 'Боттл Біч',
      'Thong Nai Pan': 'Тонг Най Пан',
      'Amsterdam Bar': 'Амстердам Бар'
    },
    footer: {
      rights: 'Усі права захищені'
    }
  }
};

/**
 * Gets a translation by key path for the specified language
 * @param {string} keyPath - Dot-notation key path (e.g., 'common.heroTitle')
 * @param {string} lang - Language code
 * @returns {string} Translated text or key path if not found
 */
export const getTranslation = (keyPath, lang = 'en') => {
  try {
    const keys = keyPath?.split('.');
    let value = translations?.[lang] || translations?.en;
    
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) {
        console.warn(`Translation not found for key: ${keyPath} in language: ${lang}`);
        return keyPath;
      }
    }
    
    return value;
  } catch (error) {
    console.error(`Error getting translation for ${keyPath}:`, error);
    return keyPath;
  }
};